import apiSlice from "../apiSlice";
import type { HomeResponseType, ProductDetailType } from "src/types/products/ProductResponses";
import type { ProductItemType } from "src/types/products/ProductItem";
import type { ProductsQueryArgs } from "src/store/api/products/productsApiSlice";
import type {
    WishlistActionPayload,
    WishlistActionResponse,
    WishlistListResponseType,
    WishlistQueryArg,
    WishlistSyncPayload,
    WishlistSyncResponse,
} from "src/types/wishlist/Wishlist";
import type { CategoryProductsQueryArgs } from "src/store/api/cats/catsApiSlice";
import type { CityItemType } from "src/types/delivery/delivery";

const WISHLIST_LIST_TAG = { type: "Wishlist" as const, id: "LIST" };
const PROFILE_TAG = { type: "Profile" as const, id: "CURRENT" };

const updateWishlistFlagInProducts = (
    products: ProductItemType[] | undefined,
    productID: number,
    inWishlist: boolean,
) => {
    products?.forEach((product) => {
        if (product.id === productID) {
            product.in_wishlist = inWishlist;
        }
    });
};

const syncWishlistFlagBySlug = (
    products: ProductItemType[] | undefined,
    wishlistSlugs: Set<string>,
) => {
    products?.forEach((product) => {
        product.in_wishlist = wishlistSlugs.has(product.slug);
    });
};

const removeWishlistProductFromPage = (
    draft: WishlistListResponseType,
    productID: number,
) => {
    const previousLength = draft.results.length;
    draft.results = draft.results.filter((product) => product.id !== productID);
    if (draft.results.length !== previousLength) {
        draft.count = Math.max(0, draft.count - 1);
        if (draft.count === 0) {
            draft.last_page = 1;
            draft.next = null;
            draft.previous = null;
        }
    }
};

const patchProductCache = (
    endpointName: string,
    draft: WishlistListResponseType | ProductDetailType | ProductItemType[] | HomeResponseType,
    action: WishlistActionPayload["action"],
    productID: number,
) => {
    const inWishlist = action === "add";

    switch (endpointName) {
        case "wishlist":
            if (action === "remove") {
                removeWishlistProductFromPage(draft as WishlistListResponseType, productID);
            } else {
                updateWishlistFlagInProducts((draft as WishlistListResponseType).results, productID, inWishlist);
            }
            break;
        case "products":
        case "catProducts":
            updateWishlistFlagInProducts((draft as WishlistListResponseType).results, productID, inWishlist);
            break;
        case "randomProducts":
            updateWishlistFlagInProducts(draft as ProductItemType[], productID, inWishlist);
            break;
        case "home": {
            const homeDraft = draft as HomeResponseType;
            updateWishlistFlagInProducts(homeDraft.latest, productID, inWishlist);
            updateWishlistFlagInProducts(homeDraft.offers, productID, inWishlist);
            updateWishlistFlagInProducts(homeDraft.best, productID, inWishlist);
            break;
        }
        case "productRetrieve": {
            const detailDraft = draft as ProductDetailType;
            if (detailDraft.id === productID) {
                detailDraft.in_wishlist = inWishlist;
            }
            updateWishlistFlagInProducts(detailDraft.related_products, productID, inWishlist);
            break;
        }
        default:
            break;
    }
};

const syncWishlistCache = (
    endpointName: string,
    draft: WishlistListResponseType | ProductDetailType | ProductItemType[] | HomeResponseType,
    wishlistSlugs: Set<string>,
) => {
    switch (endpointName) {
        case "wishlist":
            syncWishlistFlagBySlug((draft as WishlistListResponseType).results, wishlistSlugs);
            break;
        case "products":
        case "catProducts":
            syncWishlistFlagBySlug((draft as WishlistListResponseType).results, wishlistSlugs);
            break;
        case "randomProducts":
            syncWishlistFlagBySlug(draft as ProductItemType[], wishlistSlugs);
            break;
        case "home": {
            const homeDraft = draft as HomeResponseType;
            syncWishlistFlagBySlug(homeDraft.latest, wishlistSlugs);
            syncWishlistFlagBySlug(homeDraft.offers, wishlistSlugs);
            syncWishlistFlagBySlug(homeDraft.best, wishlistSlugs);
            break;
        }
        case "productRetrieve": {
            const detailDraft = draft as ProductDetailType;
            detailDraft.in_wishlist = wishlistSlugs.has(detailDraft.slug);
            syncWishlistFlagBySlug(detailDraft.related_products, wishlistSlugs);
            break;
        }
        default:
            break;
    }
};

export interface RegisterPayload {
    username: string;
    email: string;
    phone_number: string;
    password: string;
    recaptcha_token: string;
}

export interface RegisterResponse {
    public_code: string;
    identifier: string;
    username: string;
    expires_at: string;
    resend_available_at: string;
}

export interface ActivationPayload {
    public_code: string;
    secret_code: string;
    recaptcha_token: string;
}

export interface ActivationResponse { msg: string; }

export interface ResendActivationPayload {
    public_code?: string;
    identifier?: string;
    email?: string;
    device_id?: string;
    recaptcha_token: string;
}

export interface ResendActivationResponse {
    msg: string;
    public_code?: string;
    identifier?: string;
    username?: string;
    expires_at?: string | null;
    resend_available_at?: string | null;
    retry_after?: number;
}

export interface PasswordRecoveryPayload {
    email: string;
    recaptcha_token: string;
}

export interface PasswordRecoveryResponse {
    msg: string;
    public_code: string;
}

export interface PasswordConfirmationPayload {
    public_code: string;
    secret_code: string;
    recaptcha_token: string;
}

export interface PasswordConfirmationResponse {
    public_code: string;
    reset_key: string;
}

export interface PasswordResetPayload {
    public_code: string;
    password: string;
    confirm_password: string;
    recaptcha_token: string;
    reset_key?: string;
    secret_code?: string;
}

export interface PasswordResetResponse {
    msg: string;
}

export interface UserProfileResponse {
    username: string;
    fullname: string | null;
    email: string;
    phone_number: string | null;
    address: string | null;
    city: CityItemType | null;
    cities: CityItemType[];
}

export interface UpdateProfilePayload {
    username: string;
    fullname: string;
    phone_number: string;
    address: string;
    city: number | null;
    recaptcha_token: string;
}

export interface DeleteAccountResponse {
    msg: string;
    deleted: boolean;
}

const updateUserDetailsCache = apiSlice.util.updateQueryData as unknown as (
    endpointName: "userDetails",
    args: void,
    updateRecipe: (draft: UserProfileResponse) => UserProfileResponse | void,
) => ReturnType<typeof apiSlice.util.updateQueryData>;


export const accountsApiSlice = apiSlice.injectEndpoints({
    endpoints: (build) => ({
        wishlist: build.query<WishlistListResponseType, WishlistQueryArg>({
            keepUnusedDataFor: 60,
            query: (queryString) => (`/accounts/wishlist/${queryString || ""}`),
            providesTags: (result) => result ? [
                ...result.results.map((product) => ({ type: "Product" as const, id: product.id })),
                WISHLIST_LIST_TAG,
            ] : [WISHLIST_LIST_TAG],
        }),
        wishlistAction: build.mutation<WishlistActionResponse, WishlistActionPayload>({
            query: ({ action, product_id }: WishlistActionPayload) => {
                return {
                    url: "/accounts/wishlist/",
                    method: "POST",
                    body: {
                        action: action,
                        product_id: product_id
                    }
                }
            },
            async onQueryStarted({ action, product_id }, { dispatch, getState, queryFulfilled }) {
                const updateQueryData = apiSlice.util.updateQueryData as any;
                const matchingQueries = apiSlice.util.selectInvalidatedBy(
                    getState(),
                    [{ type: "Product", id: product_id }, WISHLIST_LIST_TAG],
                );

                const patches = matchingQueries
                    .map(({ endpointName, originalArgs }) => {
                        switch (endpointName) {
                            case "wishlist":
                                return dispatch(updateQueryData(
                                    "wishlist",
                                    originalArgs as WishlistQueryArg,
                                    (draft: WishlistListResponseType) => patchProductCache(endpointName, draft, action, product_id),
                                ));
                            case "products":
                                return dispatch(updateQueryData(
                                    "products",
                                    originalArgs as ProductsQueryArgs | void,
                                    (draft: WishlistListResponseType) => patchProductCache(endpointName, draft, action, product_id),
                                ));
                            case "catProducts":
                                return dispatch(updateQueryData(
                                    "catProducts",
                                    originalArgs as CategoryProductsQueryArgs,
                                    (draft: WishlistListResponseType) => patchProductCache(endpointName, draft, action, product_id),
                                ));
                            case "randomProducts":
                                return dispatch(updateQueryData(
                                    "randomProducts",
                                    originalArgs as void,
                                    (draft: ProductItemType[]) => patchProductCache(endpointName, draft, action, product_id),
                                ));
                            case "home":
                                return dispatch(updateQueryData(
                                    "home",
                                    originalArgs as void,
                                    (draft: HomeResponseType) => patchProductCache(endpointName, draft, action, product_id),
                                ));
                            case "productRetrieve":
                                return dispatch(updateQueryData(
                                    "productRetrieve",
                                    originalArgs as string,
                                    (draft: ProductDetailType) => patchProductCache(endpointName, draft, action, product_id),
                                ));
                            default:
                                return null;
                        }
                    })
                    .filter(Boolean);

                try {
                    await queryFulfilled;
                    dispatch(apiSlice.util.invalidateTags([WISHLIST_LIST_TAG]));
                } catch (error) {
                    patches.forEach((patch) => patch?.undo());
                }
            },
        }),
        wishlistClear: build.mutation<{ performed: string; cleared: number }, void>({
            query: () => ({
                url: "/accounts/wishlist/",
                method: "DELETE",
            }),
            invalidatesTags: [WISHLIST_LIST_TAG, { type: "Product" as const, id: "LIST" }],
        }),
        wishlistSync: build.mutation<WishlistSyncResponse, WishlistSyncPayload>({
            query: ({ slugs }) => ({
                url: "/accounts/wishlist/sync/",
                method: "POST",
                body: { slugs },
            }),
            async onQueryStarted(_payload, { dispatch, getState, queryFulfilled }) {
                const updateQueryData = apiSlice.util.updateQueryData as any;
                const selectCachedArgsForQuery = apiSlice.util.selectCachedArgsForQuery as any;

                try {
                    const { data } = await queryFulfilled;
                    const wishlistSlugs = new Set(data.wishlist_slugs);

                    const productsArgs = selectCachedArgsForQuery(getState(), "products");
                    const categoryProductsArgs = selectCachedArgsForQuery(getState(), "catProducts");
                    const randomProductsArgs = selectCachedArgsForQuery(getState(), "randomProducts");
                    const homeArgs = selectCachedArgsForQuery(getState(), "home");
                    const productRetrieveArgs = selectCachedArgsForQuery(getState(), "productRetrieve");

                    productsArgs.forEach((arg: ProductsQueryArgs | void) => {
                        dispatch(updateQueryData(
                            "products",
                            arg as ProductsQueryArgs | void,
                            (draft: WishlistListResponseType) => syncWishlistCache("products", draft, wishlistSlugs),
                        ));
                    });

                    categoryProductsArgs.forEach((arg: CategoryProductsQueryArgs) => {
                        dispatch(updateQueryData(
                            "catProducts",
                            arg as CategoryProductsQueryArgs,
                            (draft: WishlistListResponseType) => syncWishlistCache("catProducts", draft, wishlistSlugs),
                        ));
                    });

                    randomProductsArgs.forEach((arg: void) => {
                        dispatch(updateQueryData(
                            "randomProducts",
                            arg as void,
                            (draft: ProductItemType[]) => syncWishlistCache("randomProducts", draft, wishlistSlugs),
                        ));
                    });

                    homeArgs.forEach((arg: void) => {
                        dispatch(updateQueryData(
                            "home",
                            arg as void,
                            (draft: HomeResponseType) => syncWishlistCache("home", draft, wishlistSlugs),
                        ));
                    });

                    productRetrieveArgs.forEach((arg: string) => {
                        dispatch(updateQueryData(
                            "productRetrieve",
                            arg as string,
                            (draft: ProductDetailType) => syncWishlistCache("productRetrieve", draft, wishlistSlugs),
                        ));
                    });

                    dispatch(apiSlice.util.invalidateTags([WISHLIST_LIST_TAG]));
                } catch (_error) {
                    return;
                }
            },
        }),
        register: build.mutation<RegisterResponse, RegisterPayload>({
            query: (bodyData) => {
                return {
                    url: "/accounts/register/",
                    method: "POST",
                    body: bodyData
                }
            }
        }),
        activate: build.mutation<ActivationResponse, ActivationPayload>({
            query: (bodyData) => {
                return {
                    url: "/accounts/activation/",
                    method: "POST",
                    body: bodyData
                }
            }
        }),
        resendActivation: build.mutation<ResendActivationResponse, ResendActivationPayload>({
            query: (bodyData) => {
                return {
                    url: "/accounts/resend/activation/",
                    method: "POST",
                    body: bodyData
                }
            }
        }),
        passwordRecovery: build.mutation<PasswordRecoveryResponse, PasswordRecoveryPayload>({
            query: (bodyData) => {
                return {
                    url: "/accounts/password/recovery/",
                    method: "POST",
                    body: bodyData
                }
            }
        }),
        passwordConfirm: build.mutation<PasswordConfirmationResponse, PasswordConfirmationPayload>({
            query: (bodyData) => {
                return {
                    url: "/accounts/password/confirmation/",
                    method: "POST",
                    body: bodyData
                }
            }
        }),
        passwordReset: build.mutation<PasswordResetResponse, PasswordResetPayload>({
            query: (bodyData) => {
                return {
                    url: "/accounts/password/reset/",
                    method: "POST",
                    body: bodyData
                }
            }
        }),
        userDetails: build.query<UserProfileResponse, void>({
            query: () => "/accounts/profile/",
            providesTags: [PROFILE_TAG],
        }),
        updateProfile: build.mutation<UserProfileResponse, UpdateProfilePayload>({
            query: (bodyData) => {
                return {
                    url: "/accounts/profile/",
                    method: "PATCH",
                    body: bodyData
                }
            },
            async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(updateUserDetailsCache("userDetails", undefined, () => data));
                } catch (_error) {
                    return;
                }
            },
            invalidatesTags: [PROFILE_TAG],
        }),
        deleteAccount: build.mutation<DeleteAccountResponse, void>({
            query: () => {
                return {
                    url: "/accounts/delete/",
                    method: "DELETE"
                }
            },
            invalidatesTags: [PROFILE_TAG, WISHLIST_LIST_TAG],
        })
    })
})


export const { useWishlistActionMutation, useRegisterMutation,
    useActivateMutation, usePasswordRecoveryMutation,
    usePasswordConfirmMutation, usePasswordResetMutation,
    useUserDetailsQuery, useUpdateProfileMutation,
    useDeleteAccountMutation,
    useWishlistSyncMutation,
    useWishlistClearMutation,
    useWishlistQuery,
    useResendActivationMutation } = accountsApiSlice;
